/**
 * Shared User Vuex module for Portal and Admin Applications
 * @module UserStore
 * @author Scott Gingerysty (Fresche Solutions)
 * @date May 2020
 */

import { getField, updateField } from "vuex-map-fields";
import portalapi from "./portalapi";
import { clearApiCache } from '@/utils/filterApiCache';
import { resetStateData } from "@/utils/vuexStoreUtils";

const DEFAULT_STATE = {
  userData: {},
  selectedUser: {},
  loggedIn: false,
  portalHomepage: "",
  hasAlertedNccRequired: false,
};

const state = structuredClone(DEFAULT_STATE);

const getters = {
  getField,
  userDisplayName: (state) => {
    let firstName = state.userData.firstName;
    let lastName = state.userData.lastName;
    return `${firstName} ${lastName}`;
  },
  userStoreName: (state) => {
    return state.userData.storeName;
  },
  getUserData: (state) => {
    return state.userData;
  },
  getLoggedIn: (state) => {
    return state.loggedIn;
  },
  getUserHomepage: (state) => {
    return state.portalHomepage;
  },
  getHasAlertedNccRequired: (state) => {
    return state.hasAlertedNccRequired;
  },
};

const mutations = {
  updateField, // for vuex-map-fields

  async clearStateData(st) {
    st = resetStateData(st, DEFAULT_STATE);
  },

  setLoggedIn(state, payload) {
    state.loggedIn = payload;
  },
  setUserData(state, payload) {
    state.userData = payload;
  },
  setUserHomepage(state, page) {
    state.portalHomepage = page;
  },
  setHasAlertedNccRequired(state, payload) {
    state.hasAlertedNccRequired = payload;
  },

  setDefaultPageSize(state, pageSize) {
    state.userData.defaultPageSize = pageSize;
  }
};

const actions = {
  async login({ commit, dispatch }, loginData) {
    var response = await portalapi.appPortal.login(loginData);

    if (response.data.status == "success") {
      // Save token to session storage
      sessionStorage.setItem('token', response.data.token);
      sessionStorage.setItem('loginDate', new Date());

      commit("setUserData", response.data.user);
      commit("setLoggedIn", true);

      //Check for user login notification
      dispatch("Notifications/checkUserLoginNotifications", null, { root: true });

      // If user is authorized to regular catalog set it as homepage, otherwise use New Items Catalog 
      if (response.data.user.userAuthorities.includes("RegularCatalog")) {
        commit("setUserHomepage", "Regular Catalog");
      } else {
        commit("setUserHomepage", "New Items Catalog");
      }
    }
    return response;
  },

  async fetchUserData({ commit }) {
    var response = await portalapi.appPortal.fetchUserData();
    if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
      commit("setUserData", response.data);
      commit("setLoggedIn", true);

      // For now assume default
      commit("setUserHomepage", "Regular Catalog");
    } else {
      commit("setLoggedIn", false);
    }
    return response;
  },

  async logout({ dispatch, commit }) {
    var response = await portalapi.appPortal.logout();

    if (response.data.status == "success") {
      await dispatch("clearSessionStorageData", null, { root: true });
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('loginDate');
      sessionStorage.removeItem('vuex');

      // Reset any cached API promises
      clearApiCache();
    }
  },

  /**
   * Update password
   */
  async updatePassword({ commit }, updateData) {
    return await portalapi.appPortal.updatePassword(updateData);
  },

  /**
   * Update narcotic code
   */
  async updateNarcoticCode({ commit }, updateData) {
    return await portalapi.appPortal.updateNarcoticCode(updateData);
  },

  /**
   * Update user email
   */
  async updateUserEmail({ commit }, emailData) {
    return await portalapi.appPortal.setUserEmail(emailData);
  },

  /**
   * Update user's default show legend preference
   */
  async updateShowLegendPreference({ commit }, userData) {
    return await portalapi.moduleWebUsers.setUserShowLegendPreference(userData);
  },

  /**
   * Update user's default page size preference
   */
  async updatePageSizePreference({ commit }, userData) {
    commit("setDefaultPageSize", userData.defaultPageSize);

    return await portalapi.moduleWebUsers.setUserPageSizePreference(userData);
  },

  async resetPassword({ commit }, resetData) {
    return await portalapi.appPortal.resetPassword(resetData);
  },

  /**
   * Submits API request for a reset password email
   */
  async sendResetPasswordEmail({ commit }, payload) {
    return await portalapi.appPortal.forgotPasswordRequest(payload);
  },

  updateHasAlertedNccRequired({ commit }, payload) {
    commit("setHasAlertedNccRequired", payload);
  },
};

export default {
  namespaced: "true",
  state,
  getters,
  mutations,
  actions,
};
